import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  TextField,
  Paper,
  inputBaseClasses,
  outlinedInputClasses,
} from "@mui/material";
import { makeStyles } from "@material-ui/core";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";

const useStyles = makeStyles((theme) => ({
  autocomplete: {
    width: 140,
    [`& .${inputBaseClasses.root}`]: {
      padding: "2px 8px",
      fontSize: "0.75rem",
    },
    [`& .${outlinedInputClasses.notchedOutline}`]: {
      borderColor: grayColor[6],
    },
  },
  paper: {
    fontSize: "0.75rem",
  },
  option: {
    fontSize: "0.75rem",
    padding: theme.spacing(0.5, 1),
  },
}));

const TagInput = ({ availableTags, currentTags, onSelect, onClose }) => {
  const classes = useStyles();
  const { t } = useTranslation("translation", { keyPrefix: "rgm.inputs" });
  const [inputValue, setInputValue] = useState("");

  // Filter out tags already assigned to this variant
  const filteredOptions = availableTags.filter(
    (tag) => !currentTags.includes(tag),
  );

  const handleChange = (event, newValue) => {
    if (newValue) {
      onSelect(newValue);
    }
  };

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      onClose();
    }
    // Note: Enter key is handled by Autocomplete's onChange with freeSolo mode
  };

  return (
    <Autocomplete
      className={classes.autocomplete}
      options={filteredOptions}
      freeSolo
      autoFocus
      openOnFocus
      size="small"
      value={null}
      inputValue={inputValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      onBlur={onClose}
      filterOptions={(options, { inputValue }) => {
        const filtered = options.filter((option) =>
          option.toLowerCase().includes(inputValue.toLowerCase()),
        );

        // Add "Create" option if input doesn't match existing
        if (
          inputValue.trim() &&
          !options.some(
            (opt) => opt.toLowerCase() === inputValue.toLowerCase(),
          ) &&
          !currentTags.some(
            (tag) => tag.toLowerCase() === inputValue.toLowerCase(),
          )
        ) {
          filtered.push(`${t("create_tag", { name: inputValue.trim() })}`);
        }

        return filtered;
      }}
      getOptionLabel={(option) => option}
      renderOption={(props, option) => (
        <li {...props} className={classes.option}>
          {option}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={t("add_tag")}
          variant="outlined"
          autoFocus
          onKeyDown={handleKeyDown}
        />
      )}
      slots={{
        paper: (props) => <Paper {...props} className={classes.paper} />,
      }}
    />
  );
};

TagInput.propTypes = {
  availableTags: PropTypes.array.isRequired,
  currentTags: PropTypes.array.isRequired,
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TagInput;
